// SeverityThreads.js (Updated for better theme support)
import React from 'react';
import { Card, CardHeader, CardBody, Button, Row, Col } from 'reactstrap';
import { postureGlyphStyle, postureIconChipStyle, postureIconStyle } from "./iconStyles";

const SeverityThreads = ({ threatData }) => {
  // Handle empty or missing data
  if (!threatData || threatData.length === 0) {
    return (
      <Card style={{ background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
          <h5 className="mb-0">Top 5 High-Severity Threats</h5>
          <span style={postureIconChipStyle("#FF5B00")}>
            <i className="ri-bar-chart-line" style={postureIconStyle("#FF5B00")} />
          </span>
        </CardHeader>
        <CardBody>
          <div className="text-center py-4">
            <p className="text-muted">No threat data available</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Map asset_criticality to severity for filtering
  const getSeverityFromCriticality = (criticality) => {
    switch (criticality) {
      case 'Critical':
        return 'Critical';
      case 'High':
        return 'High';
      case 'Medium':
        return 'Medium';
      case 'Low':
        return 'Low';
      default:
        return 'Medium';
    }
  };

  // Filter and sort threats by asset criticality (treating as severity)
  const highSeverityThreats = threatData
    .filter(threat => {
      const severity = getSeverityFromCriticality(threat.asset_criticality);
      return severity === "Critical" || severity === "High";
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Sort by timestamp descending
    .slice(0, 5);

  const getIconClass = (criticality) => {
    const severity = getSeverityFromCriticality(criticality);
    switch (severity) {
      case "Critical":
        return "ri-error-warning-line text-danger";
      case "High":
        return "ri-alert-line text-primary";
      default:
        return "ri-information-line text-warning";
    }
  };

  const getIconColor = (criticality) => {
    const severity = getSeverityFromCriticality(criticality);
    switch (severity) {
      case "Critical": return "#dc2626";
      case "High": return "#ea580c";
      case "Medium": return "#d97706";
      default: return "#16a34a";
    }
  };

  const getSeverityColor = (criticality) => {
    const severity = getSeverityFromCriticality(criticality);
    switch (severity) {
      case "Critical": return { color: "#dc2626", backgroundColor: "#fef2f2", border: "1px solid #fecaca" };
      case "High":     return { color: "#ea580c", backgroundColor: "#fff7ed", border: "1px solid #fed7aa" };
      case "Medium":   return { color: "#d97706", backgroundColor: "#fffbeb", border: "1px solid #fde68a" };
      case "Low":      return { color: "#16a34a", backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0" };
      default:         return { color: "#374151", border: "1px solid #d1d5db" };
    }
  };

  // Format timestamp for display
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
      <Card style={{ background: "var(--vz-secondary-bg, #fff)", border: "1px solid var(--vz-border-color)", borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
      <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <h5 className="mb-0">Top 5 High-Severity Threats</h5>
        <span style={postureIconChipStyle("#FF5B00")}>
          <i className="ri-bar-chart-line" style={postureIconStyle("#FF5B00")} />
        </span>
      </CardHeader>
      <CardBody>
        {highSeverityThreats.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No high-severity threats found</p>
          </div>
        ) : (
          highSeverityThreats.map((threat, idx) => (
            <Row key={threat.id || idx} className="align-items-center mb-3 pb-3 border-bottom">
              <Col xs={1}>
                <span style={postureIconChipStyle(getIconColor(threat.asset_criticality))}>
                  <i className={getIconClass(threat.asset_criticality)} style={postureGlyphStyle} />
                </span>
              </Col>
              <Col xs={7}>
                <div>
                  <strong>{threat.threat_type}</strong>
                  <br />
                  <small className="text-muted">{threat.threat_description}</small>
                  <br />
                  <small className="text-muted font-monospace d-inline-flex align-items-center gap-1">
                    <span>
                      <i className="ri-map-pin-line" style={postureGlyphStyle}></i>
                    </span>
                    {threat.source_ip}
                  </small>
                  <br />
                  <small className="text-muted d-inline-flex align-items-center gap-1">
                    <span >
                      <i className="ri-time-line" style={postureGlyphStyle}></i>
                    </span>
                    {formatTime(threat.timestamp)}
                  </small>
                </div>
              </Col>
              <Col xs="auto">
                <span className="badge rounded-pill py-2 px-3" style={getSeverityColor(threat.asset_criticality)}>
                  {getSeverityFromCriticality(threat.asset_criticality)}
                </span>
              </Col>
              <Col className="text-end">
                <Button
                  color="link"
                  size="sm"
                  tag="a"
                  href={`/ThreatGuard?id=${threat.threat_id}`}
                >
                  Details
                </Button>
              </Col>
            </Row>
          ))
        )}

        <div className="text-end mt-4">
          <Button 
            color="link" 
            className="text-danger"
            tag="a"
            href="/ThreatFeed"
          >
            View All Threats
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default SeverityThreads;
